import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Calendar} from "primereact/calendar";
import {ChronoUnit} from "../../../utils/time-utils";

type CalendarWithDebounceProps = {
    value: Date
    dateFrequency?: ChronoUnit
    debounceTimeInMs?: number
    dateFormat?: string
    className?: string
    id?: string;

    onChange(date: Date): void
}

const CalendarWithDebounce = ({
                                  value,
                                  dateFrequency = "MINUTES" as ChronoUnit,
                                  onChange,
                                  className,
                                  id,
                                  debounceTimeInMs = 500,
                                  dateFormat = "yy-mm-dd",
                              }: CalendarWithDebounceProps) => {
    const [date, setDate] = useState<Date>(value);
    const [inputFocus, setInputFocus] = useState<boolean>(false);
    const timeoutId = useRef<NodeJS.Timeout | undefined>(undefined);

    useEffect(() => {
        setDate(value)
    }, [value])

    useEffect(() => {
        if (inputFocus) {
            window.addEventListener("keydown", handleEnterPressed);
        } else {
            timeoutId.current = setTimeout(() => {
                if(date !== value) {
                    onChange(date);
                }
            }, debounceTimeInMs);
        }

        return () => {
            window.removeEventListener("keydown", handleEnterPressed);
            clearTimeout(timeoutId.current);
        };
    }, [inputFocus, date, debounceTimeInMs]);

    const handleEnterPressed = useCallback((event: KeyboardEvent) => {
        if (event.code === "Enter" || event.code === "NumpadEnter") {
            setInputFocus(false);
        }
    }, []);

    const onCalendarDatepickerHandler = (pickedDate: Date | undefined) => {
        if (!pickedDate) {
            return
        }
        if (dateFrequency === "DAYS") {
            pickedDate.setHours(0);
            pickedDate.setMinutes(0);
        }

        if (dateFrequency === "HOURS") {
            pickedDate.setMinutes(0);
        }

        pickedDate.setSeconds(0)
        pickedDate.setMilliseconds(0)

        setDate(pickedDate);
    }

    const onInputHandler = () => {
        setInputFocus(true)
    }

    return <Calendar dateFormat={dateFormat}
                     className={className}
                     value={date}
                     onChange={(e) => onCalendarDatepickerHandler(e.value as Date | undefined)}
                     showTime
                     showIcon
                     iconPos="left"
                     id={id}
                     onInput={onInputHandler}
                     onBlur={() => setInputFocus(false)}
    />
}

export default CalendarWithDebounce;